/**
 * RhymeLegend - Accessible legend explaining rhyme category visual language
 *
 * Shows color swatches, border styles, and descriptions for all rhyme categories.
 * Provides WCAG-compliant documentation of the multi-channel visual encoding system.
 *
 * Design token reference: docs/design/feedback_system/design_tokens.json
 * Styles from: frontend/src/styles/rhyme-visual-language.css
 */

import { useState } from 'react'
import { RHYME_CATEGORY_STYLES } from './rhymeCategoryStyles'

interface RhymeLegendProps {
  /** Whether legend is visible by default */
  defaultExpanded?: boolean
}

/**
 * RhymeLegend Component
 *
 * Accessibility features:
 * - aria-labelledby for legend heading
 * - data-testid hooks for automated testing
 * - Keyboard accessible toggle button
 * - WCAG AA contrast information displayed
 *
 * Visual encoding documentation:
 * - Color swatches matching label backgrounds
 * - Border style indicators (solid/dashed/dotted)
 * - Category names and descriptions
 * - Font weight indicators
 */
export default function RhymeLegend({ defaultExpanded = true }: RhymeLegendProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  return (
    <div
      className="rhyme-legend"
      data-testid="rhyme-legend"
      style={{
        marginTop: '0.75rem',
        padding: '0.75rem',
        backgroundColor: '#f9fafb',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
      }}
    >
      {/* Legend heading and toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isExpanded ? '0.75rem' : '0' }}>
        <h3
          id="rhyme-legend-heading"
          style={{
            margin: 0,
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#374151',
          }}
        >
          Rhyme Category Legend
        </h3>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="rhyme-legend-content"
          data-testid="rhyme-legend-toggle"
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.75rem',
            fontWeight: 500,
            color: '#6b7280',
            backgroundColor: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            transition: 'all 150ms ease',
          }}
        >
          {isExpanded ? 'Hide' : 'Show'}
        </button>
      </div>

      {/* Legend content */}
      {isExpanded && (
        <div
          id="rhyme-legend-content"
          aria-labelledby="rhyme-legend-heading"
          style={{
            display: 'grid',
            gap: '0.5rem',
          }}
        >
          {RHYME_CATEGORY_STYLES.map((style) => (
            <div
              key={style.category}
              data-testid={`rhyme-legend-item-${style.category.toLowerCase()}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.5rem',
                backgroundColor: '#ffffff',
                borderRadius: '0.25rem',
                border: '1px solid #e5e7eb',
              }}
            >
              {/* Color swatch with border style indicator */}
              <div
                style={{
                  position: 'relative',
                  width: '24px',
                  height: '24px',
                  backgroundColor: style.backgroundColor,
                  borderRadius: '4px',
                  border: `${style.borderStyle === 'solid' ? '2px' : style.borderStyle === 'dashed' ? '1.5px' : '1px'} ${style.borderStyle} rgba(255, 255, 255, 0.5)`,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: style.fontWeight,
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                }}
                aria-label={`${style.category} color swatch`}
              >
                A
              </div>

              {/* Category info */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
                  <span
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  >
                    {style.category.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      color: '#9ca3af',
                      fontFamily: 'monospace',
                    }}
                  >
                    WCAG: {style.wcagContrast.toFixed(2)}:1
                  </span>
                </div>
                <p
                  style={{
                    margin: '0.25rem 0 0 0',
                    fontSize: '0.75rem',
                    color: '#6b7280',
                    lineHeight: '1.3',
                  }}
                >
                  {style.description}
                </p>
                <div style={{ marginTop: '0.25rem', display: 'flex', gap: '0.75rem', fontSize: '0.7rem', color: '#9ca3af' }}>
                  <span>Border: {style.borderStyle}</span>
                  <span>Weight: {style.fontWeight}</span>
                </div>
              </div>
            </div>
          ))}

          {/* Visual encoding explanation */}
          <div
            style={{
              marginTop: '0.5rem',
              padding: '0.5rem',
              backgroundColor: '#eff6ff',
              borderRadius: '0.25rem',
              border: '1px solid #dbeafe',
            }}
          >
            <h4
              style={{
                margin: '0 0 0.25rem 0',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#1e40af',
              }}
            >
              Visual Encoding Channels
            </h4>
            <ul
              style={{
                margin: 0,
                paddingLeft: '1.25rem',
                fontSize: '0.7rem',
                color: '#1e3a8a',
                lineHeight: '1.5',
              }}
            >
              <li><strong>Letter color:</strong> Rhyme group (A, B, C, D cycle through blue, purple, green, amber)</li>
              <li><strong>Border style:</strong> Category strength (solid = strong, dashed = moderate, dotted = weak)</li>
              <li><strong>Font weight:</strong> Weight tier (700 = strongest, 400 = weakest)</li>
              <li><strong>Opacity:</strong> Stability match (higher = expected, lower = creative)</li>
              <li><strong>Subscript:</strong> Callback number for cross-section rhymes (A₁, A₂, A₃)</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
